import type { ContactAskSkip } from "./contactAsk";
import { SCENARIO_MAP, type Scenario } from "./scenario";
import type { WindowState } from "./window";

/**
 * U4 REPLYFULL (Agent Brains 5.1, Composer Spec 6, B2; decision 84a, F-01,
 * F-02, F-20). When code, never the model, appends the ratified fixed contact
 * ask to a composed reply.
 *
 * `grace` is today's rule, unchanged: the final stretch of the window, unless
 * the customer already gave a contact or declined, and never in public. It
 * carries no Class D or recent-ask check because today's rule has none, and
 * this module must not change it.
 *
 * `booking_intent` is new: a `consult.intent` on the thread is the natural
 * moment to take a number or an email. Decision 84a ratified the line for
 * grace only, so this moment ships behind ACKRA_CONTACT_MOMENTS, default off.
 * It is held back more tightly than grace: only inside the window (a closed
 * window cannot send, and a non-Meta channel has its own consent regime that
 * is not specced yet, F-20), never on Class D or an unrouted turn, and never
 * when the fixed ask is already in either of the last two shop turns.
 *
 * Pure. The line itself is `NO_ANSWER_CONTACT_ASK`, unchanged.
 */

export type ContactMoment = "grace" | "booking_intent";

export function contactCaptureMoment(i: {
  readonly window: WindowState | "not_applicable";
  readonly consultIntentOnThread: boolean;
  readonly scenario: Scenario | "unrouted";
  readonly publicReply: boolean;
  readonly skip: ContactAskSkip | null;
  readonly askedInLastShopTurns: boolean;
}): ContactMoment | null {
  if (i.publicReply) return null;
  if (i.skip !== null) return null;
  if (i.window === "grace") return "grace";
  if (!i.consultIntentOnThread) return null;
  if (i.window !== "in_window") return null;
  if (i.askedInLastShopTurns) return null;
  if (i.scenario === "unrouted") return null;
  const cls = SCENARIO_MAP[i.scenario].graduationClass;
  if (cls === "D" || cls === "n/a") return null;
  return "booking_intent";
}
